 
import { useContext, useMemo } from 'react';

import { faCheck, faClock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import Link from 'next/link';
import { Accordion, Badge, Button, Table } from 'react-bootstrap';


import { Store } from '../../../../Store';
import AskForHelp from '../AskForHelp';
import { addScarcityFullyBookedDates } from '../scarcity/addScarcityFullyBookedDates';
import useFetchUsersWaitList from '../utils/useFetchUsersWaitList';
import { GroupAvailabilityRow } from './GroupAvailabilityRow';
import {
  FetchUsersWaitListQuery,
  TripsPageQuery,
} from '@website/types/graphqlOperations';

export declare type Group = TripsPageQuery['tripByURL']['groups']['items'][0];

/**
 * Filter to only show groups before the cut-off date
 */
export function filterGroupsUpToCutOffDate(groups: Group[]): Group[] {
  return groups.filter((group) => {
    const groupStart = DateTime.fromISO(group.start.utc, { zone: 'utc' });
    const cutOff = DateTime.now().plus({ years: 3 });
    return groupStart < cutOff;
  });
}

/**
 * Split the groups up by month
 *
 * Note this also removes any private groups.
 *
 * @returns An object like this:
 * ```
 * {
 *   "January 2020": [{id: "group1", ...}],
 *   "February 2020": [{id: "group2", ...}]
 * }
 * ```
 */
export function useSplitGroupsByMonth(groups: Group[]) {
  // Create an array of months
  const groupByMonth = useMemo(() => {
    const byMonth: { [key: string]: Group[] } = {};

    groups.forEach((group) => {
      // Only show public groups
      if (group.private) return;

      const startDate = DateTime.fromISO(group.start.utc, {
        zone: group.start.zone,
      }).toFormat('MMMM yyyy');

      // Create the key if it doesn't exist
      if (!byMonth[startDate]) {
        byMonth[startDate] = [];
      }

      byMonth[startDate].push(group);
    });

    return byMonth;
  }, [groups]);

  return groupByMonth;
}

/**
 * Book Group Button
 */
export function BookGroupButton({ groupID }: { groupID: string }) {
  return (
    <Link passHref href={`/book/${groupID}`} legacyBehavior>
      <Button role="button" className="text-white m-0" variant="success">
        <strong>Book</strong>
      </Button>
    </Link>
  );
}

type MonthAvailabilityTableProps = {
  items: Group[];
  userWaitList: FetchUsersWaitListQuery['user']['waitList']['items'];
};

/**
 * Availability table for a specific month
 */
export function MonthAvailabilityTable({
  items,
  userWaitList,
}: MonthAvailabilityTableProps) {
  return (
    <>
      {items.map((group) => (
        <GroupAvailabilityRow
          group={group}
          key={group.id + group.start.utc}
          userWaitList={userWaitList}
        />
      ))}
    </>
  );
}

/**
 * Groups Scarcity Alert
 *
 * @returns
 * A message like "Popular: 7/8 groups are fully booked" when lots of dates are booked.
 */
export function GroupsScarcityAlert({
  groups,
}: {
  groups: Array<Pick<Group, 'remainingSpaces'>>;
}) {
  const numberGroups = groups.length;
  const numberGroupsFullyBooked = groups.filter(
    (group) => group.remainingSpaces === 0,
  ).length;

  if (numberGroups > 2 && numberGroupsFullyBooked / numberGroups > 0.4) {
    return (
      <Badge bg="secondary" pill className="mb-1 me-1">
        <FontAwesomeIcon icon={faClock} className="me-1" />
        <span>
          Popular: {numberGroupsFullyBooked}/{numberGroups} dates are fully
          booked
        </span>
      </Badge>
    );
  }

  return null;
}

interface GroupsAvailabilityProps {
  groups: TripsPageQuery['tripByURL']['groups']['items'];
  trip: Pick<TripsPageQuery['tripByURL'], 'title'>;
}

/**
 * Trip Availability
 *
 * Shows public groups in a table, segmented by month, with the price and availability.
 */
export default function GroupsAvailability({
  groups,
  trip,
}: GroupsAvailabilityProps) {
  const { state } = useContext(Store);
  const isHost = !!state.user.host_roles?.[0];

  const groupsBeforeCutOff = filterGroupsUpToCutOffDate(groups);

  const groupsWithScarcity = addScarcityFullyBookedDates(
    groupsBeforeCutOff,
    !isHost,
  );

  const groupsByMonth = useSplitGroupsByMonth(groupsWithScarcity);

  const userWaitList = useFetchUsersWaitList();

  // Return each month as an accordion item with a table inside
  const items = Object.keys(groupsByMonth).map((month) => (
    <Accordion.Item key={month} eventKey={month} className="mb-1">
      <Accordion.Header>
        <strong>{month}</strong>
      </Accordion.Header>
      <Accordion.Body style={{ padding: 0 }}>
        <Table className="mb-0">
          <tbody>
            <MonthAvailabilityTable
              items={groupsByMonth[month]}
              userWaitList={userWaitList}
            />
          </tbody>
        </Table>
      </Accordion.Body>
    </Accordion.Item>
  ));

  //  Handle no groups scenario
  const hasAvailableGroups =
    groups.filter((group) => !group.private).length > 0;
  if (!hasAvailableGroups) {
    return (
      <div className="mt-4">
        <p>Sorry - we currently have no available dates.</p>

        <p>
          To enquire about upcoming trips or to arrange a private excursion,
          kindly contact us.
        </p>

        <div className="mb-4">
          <AskForHelp
            tripTitle={trip.title}
            link={`/help/contact`}
            type="host-help"
          />
        </div>
      </div>
    );
  }

  // Otherwise display the groups
  return (
    <>
      <GroupsScarcityAlert groups={groups} />
      <Badge bg="secondary" pill className="mb-1">
        <FontAwesomeIcon icon={faCheck} className="me-1" />
        <span>All trips are guaranteed to run</span>
      </Badge>
      <div className="mt-1">
        <Accordion defaultActiveKey={Object.keys(groupsByMonth)[0]}>
          {items}
        </Accordion>
      </div>
    </>
  );
}
